import { HeaderTextBlock, TypographyContainer } from "@/components/Typography";
import { useGetComplianceReportsTable } from "@/requests/hooks/collect";
import { useTranslation } from "react-i18next";
import { Table } from "@/components/Table";
import { createColumnHelper } from "@tanstack/table-core";
import { Badge } from "@/components/Badge";
import stringToTitleCase from "@/utils/stringToTitleCase";
import { formatDate } from "@/utils/helpers";
import { Button } from "@/components/Button";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import {
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/pro-duotone-svg-icons";

const ComplianceReportsView = () => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<IComplianceReport>();

  const columns = [
    columnHelper.accessor("reportType", {
      header: t("common.complianceReports.table.reportType"),
      cell: info => {
        const value = info.getValue();

        return stringToTitleCase(value)!.replaceAll("_", " ");
      },
    }),
    columnHelper.accessor("status", {
      header: t("common.complianceReports.table.status.title"),
      cell: info => {
        const status = info.getValue();

        return (
          <Badge
            variant={status === "DRAFT" ? "warning" : "success"}
            icon={status === "DRAFT" ? faExclamationCircle : faCheckCircle}>
            {t(`common.complianceReports.table.status.${status}`)}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("date", {
      header: t("common.complianceReports.table.date"),
      cell: info => {
        const { date, createdAt, reviewedAt } = info.row.original;
        const dateToShow = reviewedAt || date || createdAt;

        return formatDate(dateToShow);
      },
    }),
    columnHelper.accessor("content", {
      header: t("common.complianceReports.table.action"),
      cell: info => {
        const { status } = info.row.original;

        return (
          <Button
            as="link"
            variant="link"
            to={info.row.original.id}
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="h-15 w-15">
            {status === "COMPLETE"
              ? t("common.complianceReports.table.view")
              : t("common.complianceReports.table.action")}
          </Button>
        );
      },
    }),
  ];

  const renderHeaderTextBlock = () => (
    <TypographyContainer>
      <HeaderTextBlock
        align="left"
        featureTextColor="primary"
        headingTextColor="secondary"
        featureText={t("common.complianceReports.header.feature")}
        headingText={t("common.complianceReports.header.heading")}
        descriptionText={t("common.complianceReports.header.description")}
      />
    </TypographyContainer>
  );

  return (
    <div className="flex flex-col gap-50">
      {renderHeaderTextBlock()}
      <Table
        columns={columns}
        queryKey={["email", "compliance-reports"]}
        queryHook={useGetComplianceReportsTable}
      />
    </div>
  );
};

export default ComplianceReportsView;
